import { BaseExportModal, ExportModalProps } from './ExportModal'
import { DOORS_COLUMNS } from 'components/DataExpansionComponents/Doors/doorsColumnsInfo'
import { createExportColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { getCommonChainProxyExportColumns } from 'components/DataExpansionComponents/Common/commonExportColumns'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useDownload } from 'features/download-widget'
import { startUnifiedTargetExportV2 } from 'services/downloadsApi'
import { getDownloadStatus } from 'services/downloads'
import { useColumnStoreContext } from 'stores/ColumnsStore/useColumnsStoreContext'

export default function DoorExportModal(props: ExportModalProps) {
  const { hideIngredientsMatrix } = useFeatureFlagService()
  const doorColumnHelper = createExportColumnHelper({
    columns: DOORS_COLUMNS,
  })

  const selectedColumns = useColumnStoreContext(
    (state) => state.selectedColumns
  )
  const { startDownload } = useDownload()

  const columns = [
    {
      title: 'Target Attributes',
      elements: [
        doorColumnHelper.retrieveColumn('restaurant_name'),
        doorColumnHelper.retrieveColumn('chain_name'),
        doorColumnHelper.retrieveColumn('chain_count'),
        ...(!hideIngredientsMatrix
          ? [doorColumnHelper.retrieveColumn('ingredients_matrix')]
          : []),
        doorColumnHelper.retrieveColumn('menu_size'),
        doorColumnHelper.retrieveColumn('menu_matches'),
        doorColumnHelper.retrieveColumn('menu_url'),
        doorColumnHelper.retrieveColumn('cuisine_50'),
        doorColumnHelper.retrieveColumn('velocity_group'),
        doorColumnHelper.retrieveColumn('expense_category'),
        doorColumnHelper.retrieveColumn('dmanamecbsa'),
        doorColumnHelper.retrieveColumn('address'),
        doorColumnHelper.retrieveColumn('city'),
        doorColumnHelper.retrieveColumn('state'),
        doorColumnHelper.retrieveColumn('zip'),
        doorColumnHelper.retrieveColumn('google_place_url'),
        doorColumnHelper.retrieveColumn('phone'),
        doorColumnHelper.retrieveColumn('website'),
        doorColumnHelper.retrieveColumn('domain'),
        doorColumnHelper.retrieveColumn('hq_state1'),
        doorColumnHelper.retrieveColumn('hq_city1'),
        doorColumnHelper.retrieveColumn('hq_state2'),
        doorColumnHelper.retrieveColumn('hq_city2'),
        doorColumnHelper.retrieveColumn('door_distributors'),
        doorColumnHelper.retrieveColumn('door_gpos'),
      ],
    },
    {
      title: 'Reputation Insights',
      elements: [
        doorColumnHelper.retrieveColumn('avg_rating'),
        doorColumnHelper.retrieveColumn('total_reviews_count'),
        doorColumnHelper.retrieveColumn('instagram_url'),
        doorColumnHelper.retrieveColumn('instagram_followers'),
        doorColumnHelper.retrieveColumn('instagram_following'),
        doorColumnHelper.retrieveColumn('instagram_posts'),
        doorColumnHelper.retrieveColumn('instagram_text_percentile'),
        doorColumnHelper.retrieveColumn('reputation_data'),
        doorColumnHelper.retrieveColumn('michelin_stars'),
      ],
    },
    {
      title: 'Predicted Values and Deals',
      elements: [
        doorColumnHelper.retrieveColumn('total_ltv'),
        doorColumnHelper.retrieveColumn('brand_ltv'),
        doorColumnHelper.retrieveColumn('revenue_ltv'),
        doorColumnHelper.retrieveColumn('one_year_total_value'),
        doorColumnHelper.retrieveColumn('tabo'),
        doorColumnHelper.retrieveColumn('taro'),
        doorColumnHelper.retrieveColumn('pounds_per_year'),
        ...getCommonChainProxyExportColumns(),
      ],
    },
  ]

  const onSubmit = async (
    selectedCols: string[],
    exportType: 'csv' | 'xlsx'
  ) => {
    await startDownload({
      items: [
        {
          id: 0,
          name: 'Exporting Restaurant Doors...',
        },
      ],
      initiate: async () => {
        const { sort, ...filterWithoutSort } = (props.filters ?? {}) as Record<
          string,
          unknown
        >
        const response = await startUnifiedTargetExportV2(
          {
            selected_columns: selectedCols,
            export_type: exportType,
            filter_params: {
              ...filterWithoutSort,
              product_id: props.productSelected,
              include_ids: props.selectedIds ?? [],
              exclude_ids: props.excludeIds ?? [],
            },
            sort: sort as string | undefined,
          },
          // Restaurant is the only consumer; the unified doors export resolves
          // the select-all through the same engine that rendered the rows.
          props.unifiedTargetType ?? 'restaurant',
          true
        )
        return { type: 'async', requestId: response.request_id }
      },
      getStatus: getDownloadStatus,
      label: 'Restaurant Companies',
    })
  }

  return (
    <BaseExportModal
      title="Select Columns for Restaurant Doors Download"
      subTitle="Download up to 10,000 doors at a time, with any of the following columns. Any filters already applied will remain active."
      submitText="Export Doors"
      columns={columns}
      selectedColumns={selectedColumns}
      onSubmit={onSubmit}
      show={props.show}
      handleClose={props._handleClose}
    />
  )
}
